import {
  CookieConsentCategoryValues,
  CookieTable,
  CookieTableCategories,
  CookieTableItem,
  CreateCookieTable,
  SupportedLanguage,
} from './types';
import { expireToString } from './expiration';
import { KNOWN_COOKIE_CATEGORY_MAP } from './constants';

export const PREDEFINED_COOKIE_TABLE_URL = 'https://consents.almacareer.tech/acl-lda/categorization.json';

export interface ApiCookieEntry {
  pattern: string;
  name: string;
  provider: string;
  suggested_category: string;
  description: Record<SupportedLanguage, string>;
}

export const createCookieTable =
  (currentLanguage: SupportedLanguage): CreateCookieTable =>
  async (items: CookieTableItem[]): Promise<CookieTable> => {
    const localCategoryByName = new Map(KNOWN_COOKIE_CATEGORY_MAP.map(({ name, category }) => [name, category]));

    let data: { cookies: ApiCookieEntry[] };
    try {
      const response = await fetch(PREDEFINED_COOKIE_TABLE_URL);
      data = (await response.json()) as { cookies: ApiCookieEntry[] };
    } catch {
      data = { cookies: [] };
    }

    // Build name (glob) → API entry lookup
    const descriptionsByName = new Map<string, ApiCookieEntry>();
    for (const entry of data.cookies) {
      descriptionsByName.set(entry.name, entry);
    }

    const categories: CookieTableCategories = {
      necessary: [],
      ad: [],
      analytics: [],
      functionality: [],
      personalization: [],
    };

    for (const item of items) {
      if ('suggestedCategory' in item) {
        // CustomCookie — use provided description and expiration
        categories[item.suggestedCategory].push({
          name: item.pattern ?? item.name,
          description: item.description[currentLanguage] ?? '',
          expiration: expireToString(item.expire, currentLanguage),
        });
      } else {
        // PredefinedCookie — API entry takes priority; generated map is the category fallback
        const apiEntry = descriptionsByName.get(item.name);
        const category = (apiEntry?.suggested_category ??
          localCategoryByName.get(item.name) ??
          'necessary') as CookieConsentCategoryValues;
        categories[category].push({
          name: apiEntry?.pattern ?? item.name,
          description: apiEntry?.description[currentLanguage] ?? apiEntry?.description.en ?? '',
          expiration: expireToString(item.expire, currentLanguage),
        });
      }
    }

    return { [currentLanguage]: categories };
  };
